import React from "react";
import classNames from "classnames";
import { HiOutlineTicket } from "react-icons/hi";
import { MdClose } from "react-icons/md";

import { Button } from "@/components/Button";
import { Chip } from "@/components/Chip";
import { Link } from "@/components/Link";
import { Typography } from "@/components/Typography";
import { PreviewCloudinaryImage } from "@/features/previews/shared";
import { checkValidURL } from "@/utils/formHelper";

import { CouponTicketBackBody } from "./CouponTicketBackBody";
import type { CouponTicketProps } from "./types";

const CouponTicketCtaBullets = ({ backgroundColor }: { backgroundColor: string | undefined }) => (
  <>
    <div
      className="coupon-template__ticket-side-cta__bullet coupon-template__ticket-side-cta__bullet--left"
      style={{ backgroundColor }}
    />
    <div
      className="coupon-template__ticket-side-cta__bullet coupon-template__ticket-side-cta__bullet--right"
      style={{ backgroundColor }}
    />
  </>
);

export const CouponTicket = ({
  isFlipped,
  hasLogo,
  buttonColor,
  backgroundColor,
  logoUrl,
  isInitialPreview,
  infoSalesBadge,
  infoHeadline,
  infoDescription,
  infoButtonText,
  promotionalCodeLabel,
  couponName,
  couponUrlLink,
  couponUrlText,
  dateStatus,
  copyButtonLabel,
  t,
  onFlip,
  onClose,
  onCopyCode,
}: CouponTicketProps) => {
  const ticketClasses = classNames("coupon-template__ticket", {
    "coupon-template__ticket--flipped": isFlipped,
  });

  const frontBodyClasses = classNames("coupon-template__ticket-side-body", {
    "coupon-template__ticket-side-body--without-logo": !hasLogo,
  });

  return (
    <div className={ticketClasses}>
      <div className="coupon-template__ticket-side coupon-template__ticket-side--front">
        {hasLogo && logoUrl && (
          <div className="coupon-template__ticket-side-banner">
            <PreviewCloudinaryImage
              src={logoUrl}
              alt="coupon banner"
              variant="hero"
              isInitPreview={isInitialPreview === true}
              className="coupon-template__ticket-side-banner__background"
              placeholderClass="coupon-template__ticket-side-banner__placeholder"
            />
          </div>
        )}
        <div className={frontBodyClasses}>
          {infoSalesBadge && (
            <Chip
              className="coupon-template__ticket-side-body__badge"
              label={infoSalesBadge}
              type="filled"
              color="primary"
              size="medium"
              icon={{ position: "left", component: <HiOutlineTicket aria-hidden="true" /> }}
              style={{ backgroundColor: buttonColor }}
            />
          )}
          <div className="coupon-template__ticket-side-body__info">
            <Typography
              Tag="p"
              size="xl"
              classname="coupon-template__ticket-side-body__info-headline"
            >
              {infoHeadline}
            </Typography>
            <Typography
              Tag="p"
              size="s"
              classname="coupon-template__ticket-side-body__info-description"
            >
              {infoDescription}
            </Typography>
          </div>
        </div>
        <div className="coupon-template__ticket-side-cta">
          <Button
            buttonColor={buttonColor}
            small
            label={infoButtonText}
            onClick={onFlip}
            id="coupon-ticket-flip-button"
          />
          {!isFlipped && <CouponTicketCtaBullets backgroundColor={backgroundColor} />}
        </div>
      </div>

      <div className="coupon-template__ticket-side coupon-template__ticket-side--back">
        <div className="coupon-template__ticket-side-banner">
          <Typography
            Tag="p"
            size="xs"
            classname="coupon-template__ticket-side-banner__title"
          >
            {promotionalCodeLabel}
          </Typography>
          <button
            type="button"
            className="coupon-template__ticket-side-banner__button"
            onClick={onClose}
            aria-label="Close coupon code"
          >
            <MdClose aria-hidden="true" />
          </button>
        </div>
        <div className="coupon-template__ticket-side-body">
          <CouponTicketBackBody
            couponName={couponName}
            dateStatus={dateStatus}
            buttonColor={buttonColor}
            copyButtonLabel={copyButtonLabel}
            t={t}
            onCopyCode={onCopyCode}
          />
        </div>
        <div className="coupon-template__ticket-side-cta">
          {isFlipped && <CouponTicketCtaBullets backgroundColor={backgroundColor} />}
          {couponUrlLink && (
            <Link
              className="button u-spacing-2 button--square button--small flipped-button"
              href={checkValidURL(couponUrlLink) || ""}
              style={{
                color: buttonColor,
                borderColor: buttonColor,
              }}
              externalLink
              type="button"
              prefetch={false}
            >
              <Typography
                Tag="p"
                size="s"
              >
                {couponUrlText}
              </Typography>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};
